import React from 'react';
import { gql } from '@soundxyz/gql-string';
import type { ShareModalSource } from '@soundxyz/utils/analytics';
import { CommentButtonAction, PostActionButtons } from '@components/Feed/Post/ActionButtons';
import { ReadMore } from '@components/Feed/Post/ReadMore';
import { LexicalDisplay } from '@components/Lexical/Display';
import { PlaylistPreview } from '@components/Playlists/Preview';
import { getFragment } from '@graphql/reactQuery';
import { FeedPlaylistPostFragmentDoc, type FragmentType } from '@graphql/reactQuery';
import { useLexicalValidate } from '@hooks/useLexicalValidate';
import { FeedCountSummary } from '../FeedCountSummary';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment FeedPlaylistPost on PlaylistPost {
    id
    content
    lexicalContent
    creator {
      id
      username
      publicAddress
      artist {
        id
      }
      ...UserActivityDetail
    }
    optionalShelf {
      id
      user {
        id
        twitterHandle
      }
      ...PlaylistPreview
    }
    ...FeedPostActionsInfo
    ...FeedCountSummary
  }
`);

export function FeedPlaylistPost({
  playlistPostFrag,
  commentButtonAction,
  shareButtonSource,
  source,
}: {
  commentButtonAction: CommentButtonAction;
  playlistPostFrag: FragmentType<FeedPlaylistPostFragmentDoc>;
  shareButtonSource: ShareModalSource;
  source: 'Post Page' | 'Feed Card';
}) {
  const playlistPost = getFragment(FeedPlaylistPostFragmentDoc, playlistPostFrag);

  const postContent = (
    <div className="font-base text-base-l text-base700" data-feed-clickable-div>
      {playlistPost.content}
    </div>
  );

  const readMoreEnabled = commentButtonAction === CommentButtonAction.ShowModal;

  const lexicalContent = useLexicalValidate(playlistPost.lexicalContent);
  return (
    <div className="flex flex-col gap-4" data-feed-clickable-div>
      <ReadMore maxHeightClassName="max-h-[88px] md:max-h-[112px]" enabled={readMoreEnabled}>
        {!!lexicalContent ? <LexicalDisplay state={lexicalContent} /> : postContent}
      </ReadMore>

      {playlistPost.optionalShelf ? (
        <PlaylistPreview playlistFrag={playlistPost.optionalShelf} newPlaylist={false} />
      ) : (
        <div className="flex h-[100px] w-full items-center justify-center rounded-lg bg-base50 font-base text-base-m text-base500 md:h-[160px]">
          This playlist has been deleted
        </div>
      )}
      {source === 'Post Page' && <FeedCountSummary post={playlistPost} />}

      <PostActionButtons
        postFrag={playlistPost}
        commentButtonAction={commentButtonAction}
        sourceLocation={shareButtonSource}
        resourceOwnerTwitterHandle={playlistPost.optionalShelf?.user.twitterHandle ?? null}
        iconSize={18}
        hideShareButton={false}
        source={source}
      />
    </div>
  );
}
